import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../../Context/ThemeContext";
import { useAuth } from "../../Context/auth";
import Layout from "../../Component/Layout/Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PlanPurchase = () => {
  const { darkMode } = useTheme();
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // State for form data
  const [formData, setFormData] = useState({
    transactionId: "",
    accountType: "",
    image: null,
  });

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [exchangeRate] = useState(150);
  const [previewImage, setPreviewImage] = useState(null);

  const pkrPrice = plan ? (plan.price * exchangeRate).toLocaleString() : 0;

  // Styles with dark mode support
  const styles = {
    container: {
      minHeight: "100vh",
      background: darkMode
        ? "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
        : "linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)",
      padding: "48px 16px",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    card: {
      background: darkMode ? "#2d3748" : "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      padding: "32px",
      maxWidth: "500px",
      margin: "0 auto",
      border: darkMode ? "1px solid #4a5568" : "none",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "24px",
      textAlign: "center",
    },
    planInfo: {
      background: darkMode ? "#4a5568" : "#e6f0ff",
      borderRadius: "8px",
      padding: "16px",
      marginBottom: "24px",
      borderLeft: `4px solid ${darkMode ? "#4f46e5" : "#2563eb"}`,
    },
    planName: {
      fontSize: "18px",
      fontWeight: "600",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "8px",
    },
    planPrice: {
      fontSize: "20px",
      fontWeight: "700",
      color: darkMode ? "#ffffff" : "#1a1a1a",
    },
    paymentMethodInfo: {
      background: darkMode ? "#4a5568" : "#f0f4ff",
      borderRadius: "8px",
      padding: "16px",
      marginBottom: "16px",
      borderLeft: `4px solid ${darkMode ? "#10b981" : "#059669"}`,
    },
    paymentMethodTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "8px",
    },
    paymentMethodDetail: {
      fontSize: "14px",
      color: darkMode ? "#a0aec0" : "#4a5568",
    },
    boldText: {
      fontWeight: "700",
      color: darkMode ? "#ffffff" : "#1a1a1a",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "600",
      color: darkMode ? "#a0aec0" : "#4a5568",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: darkMode ? "1px solid #4a5568" : "1px solid #cbd5e0",
      background: darkMode ? "#2d3748" : "#ffffff",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      fontSize: "14px",
      marginBottom: "16px",
    },
    select: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: darkMode ? "1px solid #4a5568" : "1px solid #cbd5e0",
      background: darkMode ? "#2d3748" : "#ffffff",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      fontSize: "14px",
      marginBottom: "16px",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='${
        darkMode ? "%23ffffff" : "%231a1a1a"
      }' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "16px 12px",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: "24px",
    },
    cancelButton: {
      padding: "12px 24px",
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "14px",
      cursor: "pointer",
      background: darkMode ? "#4a5568" : "#e2e8f0",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      border: "none",
    },
    submitButton: {
      padding: "12px 24px",
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "14px",
      cursor: "pointer",
      background: "#4f46e5",
      color: "#ffffff",
      border: "none",
    },
    disabledButton: {
      opacity: "0.7",
      cursor: "not-allowed",
    },
    instructions: {
      background: darkMode ? "#2d3748" : "#f0f4ff",
      borderRadius: "8px",
      padding: "16px",
      marginTop: "24px",
      fontSize: "14px",
      color: darkMode ? "#a0aec0" : "#4a5568",
    },
    instructionsTitle: {
      fontWeight: "600",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "12px",
    },
    instructionsList: {
      paddingLeft: "20px",
      margin: "0",
    },
    loading: {
      textAlign: "center",
      fontSize: "16px",
      color: darkMode ? "#a0aec0" : "#4a5568",
      padding: "48px",
    },
    pkrPrice: {
      fontSize: "16px",
      color: darkMode ? "#a0aec0" : "#4a5568",
      marginTop: "4px",
    },
    imageUpload: {
      marginBottom: "16px",
    },
    imagePreview: {
      maxWidth: "100%",
      maxHeight: "200px",
      marginTop: "8px",
      borderRadius: "8px",
      border: darkMode ? "1px solid #4a5568" : "1px solid #cbd5e0",
    },
    imageUploadButton: {
      display: "inline-block",
      padding: "8px 16px",
      borderRadius: "4px",
      background: darkMode ? "#4a5568" : "#e2e8f0",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      cursor: "pointer",
      fontSize: "14px",
      marginTop: "8px",
    },
  };

  // Fetch plan details and deposit accounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get plan ID from URL or location state
        const planId =
          location.state?.planId ||
          new URLSearchParams(location.search).get("planId");

        if (!planId) {
          toast.error("Plan ID not provided");
          navigate("/dashboard/user/plans");
          return;
        }

        // Fetch plan details
        const planResponse = await axios.get(
          `https://bioopay-full-backend.onrender.com/api/v1/plane/get-plane/${planId}`
        );

        if (!planResponse.data.data) {
          throw new Error("Plan not found");
        }

        setPlan(planResponse.data.data);

        // Fetch deposit accounts
        const { data } = await axios.get(
          "https://bioopay-full-backend.onrender.com/api/v1/depositeaccount/get-account",
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              Expires: "0",
            },
          }
        );

        // Set deposit accounts from data.data
        setDepositAccounts(Array.isArray(data.data) ? data.data : []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to fetch data";
        toast.error(errorMessage);

        if (err.response?.status === 404 || err.message === "Plan not found") {
          navigate("/dashboard/user/plans");
        }

        setLoading(false);
      }
    };

    fetchData();
  }, [location, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // When account type changes, find and set the selected method
    if (name === "accountType") {
      const method = depositAccounts.find((m) => m._id === value);
      setSelectedMethod(method);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create a canvas to compress the image
    const compressImage = (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions while maintaining aspect ratio
            const maxDimension = 800; // Max width/height
            if (width > height && width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            } else if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to base64 with reduced quality
            const base64String = canvas.toDataURL('image/jpeg', 0.7); // Reduce quality to 70%
            resolve(base64String);
          };
          img.src = event.target.result;
        };
        reader.readAsDataURL(file);
      });
    };

    // Validate file size (max 10MB for initial file)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size should be less than 10MB");
      return;
    }

    // Compress and set the image
    compressImage(file).then((compressedBase64) => {
      setFormData((prev) => ({
        ...prev,
        image: compressedBase64,
      }));
      setPreviewImage(compressedBase64);
    }).catch((error) => {
      console.error("Error compressing image:", error);
      toast.error("Error processing image");
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.transactionId) {
        throw new Error("Please enter transaction ID");
      }

      if (!formData.accountType) {
        throw new Error("Please select payment method");
      }

      if (!formData.image) {
        throw new Error("Please upload payment proof");
      }

      const response = await axios.post(
        "https://bioopay-full-backend.onrender.com/api/v1/deposite/create",
        {
          planId: plan._id,
          transactionId: formData.transactionId,
          image: formData.image,
        },
        {}
      );

      toast.success("Deposit submitted successfully!");
      navigate("/dashboard/user");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Submission failed";
      toast.error(errorMessage);

      if (err.response?.status === 404) {
        navigate("/dashboard/user/plans");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loading}>Loading plan details...</div>
        </div>
      </Layout>
    );
  }

  if (!plan) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.card}>
            <p style={{ ...styles.planName, marginBottom: "16px" }}>
              Plan not found
            </p>
            <button
              onClick={() => navigate("/dashboard/user/plans")}
              style={styles.submitButton}
            >
              Back to Plans
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Complete Your Purchase</h2>

          <div style={styles.planInfo}>
            <h3 style={styles.planName}>{plan.Planename} Plan</h3>
            <p style={styles.planPrice}>${plan.price} / Lifetime</p>
            <p style={styles.pkrPrice}>
              ≈ {pkrPrice} PKR (1 USD = {exchangeRate} PKR)
            </p>
          </div>

          {selectedMethod && (
            <div style={styles.paymentMethodInfo}>
              <h4 style={styles.paymentMethodTitle}>Payment Instructions:</h4>
              <p style={styles.paymentMethodDetail}>
                Please send{" "}
                <strong style={styles.boldText}>
                  ${plan.price} (≈ {pkrPrice} PKR)
                </strong>{" "}
                to:
              </p>
              <p style={styles.paymentMethodDetail}>
                <strong style={styles.boldText}>
                  {selectedMethod.account}
                </strong>
              </p>
              <p style={styles.paymentMethodDetail}>
                Account Number:{" "}
                <strong style={styles.boldText}>
                  {selectedMethod.accountNumber}
                </strong>
              </p>
              {selectedMethod.image && (
                <img
                  src={selectedMethod.image}
                  alt={selectedMethod.name}
                  style={{
                    maxWidth: "100px",
                    marginTop: "10px",
                    borderRadius: "8px",
                  }}
                />
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div>
              <label htmlFor="accountType" style={styles.label}>
                Payment Method
              </label>
              <select
                id="accountType"
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                style={styles.select}
                required
              >
                <option value="">Select payment method</option>
                {depositAccounts.map((method) => (
                  <option key={method._id} value={method._id}>
                    {method.account} ({method.accountNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="transactionId" style={styles.label}>
                Transaction ID
              </label>
              <input
                type="text"
                id="transactionId"
                name="transactionId"
                value={formData.transactionId}
                onChange={handleChange}
                placeholder="Enter your transaction ID"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.imageUpload}>
              <label htmlFor="image" style={styles.label}>
                Payment Proof (Screenshot/Receipt)
              </label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                required
                style={{ display: "none" }}
              />
              <label htmlFor="image" style={styles.imageUploadButton}>
                Choose File
              </label>
              {previewImage && (
                <div>
                  <img
                    src={previewImage}
                    alt="Payment proof preview"
                    style={styles.imagePreview}
                  />
                </div>
              )}
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={() => navigate("/dashboard/user/plans")}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  ...styles.submitButton,
                  ...(submitting ? styles.disabledButton : {}),
                }}
              >
                {submitting ? "Processing..." : "Submit Payment"}
              </button>
            </div>
          </form>

          <div style={styles.instructions}>
            <h4 style={styles.instructionsTitle}>Important Notes:</h4>
            <ol style={styles.instructionsList}>
              <li>
                Make sure to enter the exact transaction ID from your payment
              </li>
              <li>
                Upload clear screenshot or photo of payment receipt (max 2MB)
              </li>
              <li>Your payment will be verified within 24 hours</li>
              <li>Contact support if you encounter any issues</li>
            </ol>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlanPurchase;
